/**
 * RFC 8414 OAuth 2.0 Authorization Server Metadata Discovery
 *
 * Fetches SSO configuration from the server using RFC 8414 discovery endpoint.
 * Supports multiple SSO providers (Entra, Okta, Google, etc.) via server-provided metadata.
 *
 * @module lib/authentication/entra/discovery
 */

import axios from 'axios';
import config from '../../../config.json';

/**
 * Get SSO configuration for a customer via RFC 8414 discovery endpoint
 *
 * Fetches OAuth 2.0 authorization server metadata and extracts SSO provider configurations.
 * Determines available authentication methods from grant_types_supported.
 * Supports multiple SSO providers (e.g., entra_sso, okta_sso, google_sso).
 *
 * @param {string} customer - Customer name
 * @returns {Promise<Object>} Normalized SSO configuration object
 * @returns {boolean} passwordAvailable - Whether password authentication is available
 * @returns {Object} providers - Object mapping provider names to their configurations
 * @returns {string[]} availableProviders - Array of enabled provider names (e.g., ['entra'])
 * @returns {Object} [providers.entra] - Entra SSO configuration (if available)
 * @returns {boolean} [providers.entra.enabled] - Whether Entra SSO is available
 * @returns {string} [providers.entra.tenantId] - Entra tenant ID
 * @returns {string} [providers.entra.clientId] - Entra client ID
 * @returns {string} [providers.entra.authority] - Entra authority URL
 * @returns {string} [providers.entra.redirectUri] - OAuth redirect URI
 * @returns {string} [providers.entra.authorizationEndpoint] - OAuth 2.0 authorization endpoint URL
 * @returns {Object} [providers.entra.rawMetadata] - Raw server payload for debugging/extensibility
 */
// eslint-disable-next-line import/prefer-default-export
export const getSSOConfig = async customer => {
  if (!customer || customer.trim() === '') {
    return {
      passwordAvailable: false,
      providers: {},
      availableProviders: []
    };
  }
  try {
    // Construct discovery endpoint URL per RFC 8414 Section 3.1
    // RFC 8414: Well-known URI is inserted between host and path component
    // If issuer is ${baseUrl}/${customerName}, metadata is at:
    //   ${baseUrl}/.well-known/oauth-authorization-server/${customerName}
    // Use config.dataURL directly to avoid dependency cycle with ajax.js
    // Normalize and encode customer to prevent path traversal and URL manipulation
    const baseURL = config.dataURL.endsWith('/') ? config.dataURL.slice(0, -1) : config.dataURL;
    const customerSegment = encodeURIComponent(customer.trim().toLowerCase());
    const discoveryURL = `${baseURL}/.well-known/oauth-authorization-server/${customerSegment}`;

    // Fetch RFC 8414 metadata (public endpoint, no auth required)
    const response = await axios.get(discoveryURL, {
      // No auth headers needed for public discovery endpoint
      headers: {}
    });
    const metadata = response.data;

    // Determine authentication methods from grant_types_supported
    const grantTypes = metadata.grant_types_supported || [];
    const passwordAvailable = grantTypes.includes('password');

    // Extract all SSO providers from metadata
    // Server can provide multiple providers: entra_sso, okta_sso, google_sso, etc.
    const providers = {};
    const availableProviders = [];

    /**
     * Normalizes raw server metadata (snake_case) to a camelCase provider shape.
     * Returns only the normalized shape; raw payload is under rawMetadata for debugging.
     * @param {Object} raw - Raw provider metadata from server
     * @returns {Object} Normalized provider config
     */
    const normalizeProviderMetadata = raw => {
      var _raw$tenant_id, _raw$client_id, _raw$authority, _raw$redirect_uri, _ref, _raw$authorization_en;
      return {
        enabled: true,
        tenantId: (_raw$tenant_id = raw.tenant_id) !== null && _raw$tenant_id !== void 0 ? _raw$tenant_id : null,
        clientId: (_raw$client_id = raw.client_id) !== null && _raw$client_id !== void 0 ? _raw$client_id : null,
        authority: (_raw$authority = raw.authority) !== null && _raw$authority !== void 0 ? _raw$authority : null,
        redirectUri: (_raw$redirect_uri = raw.redirect_uri) !== null && _raw$redirect_uri !== void 0 ? _raw$redirect_uri : null,
        authorizationEndpoint: (_ref = (_raw$authorization_en = raw.authorization_endpoint) !== null && _raw$authorization_en !== void 0 ? _raw$authorization_en : raw.endpoint) !== null && _ref !== void 0 ? _ref : null,
        rawMetadata: raw
      };
    };

    // Known SSO provider keys (extensible for future providers)
    const ssoProviderKeys = ['entra_sso', 'okta_sso', 'google_sso', 'auth0_sso'];
    ssoProviderKeys.forEach(providerKey => {
      const providerMetadata = metadata[providerKey];
      if (providerMetadata && providerMetadata.enabled === true) {
        // Extract provider name (e.g., 'entra' from 'entra_sso')
        // Use regex to ensure we only replace '_sso' at the end of the string
        const providerName = providerKey.replace(/_sso$/, '');
        providers[providerName] = normalizeProviderMetadata(providerMetadata);
        availableProviders.push(providerName);
      }
    });

    // Also check for any unknown SSO providers (future extensibility)
    Object.keys(metadata).forEach(key => {
      if (key.endsWith('_sso') && !ssoProviderKeys.includes(key)) {
        const providerMetadata = metadata[key];
        if (providerMetadata && providerMetadata.enabled === true) {
          const providerName = key.replace(/_sso$/, '');
          providers[providerName] = normalizeProviderMetadata(providerMetadata);
          availableProviders.push(providerName);
        }
      }
    });
    return {
      passwordAvailable,
      providers,
      availableProviders
    };
  } catch (error) {
    // Handle 404 (customer not found) gracefully
    if (error.response && error.response.status === 404) {
      return {
        passwordAvailable: false,
        providers: {},
        availableProviders: []
      };
    }

    // Handle network errors gracefully
    if (error.message && (error.message.includes('Network Error') || error.message.includes('timeout'))) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch SSO configuration:', error.message);
      return {
        passwordAvailable: false,
        providers: {},
        availableProviders: []
      };
    }

    // For other errors, log and return unavailable
    // eslint-disable-next-line no-console
    console.warn('Error fetching SSO configuration:', error);
    return {
      passwordAvailable: false,
      providers: {},
      availableProviders: []
    };
  }
};